import { Injectable, inject, signal, computed } from '@angular/core';
import { AudioCaptureService } from './audio-capture.service';
import { VoiceApiService } from './voice-api.service';
import {
  VoiceState,
  INITIAL_VOICE_STATE,
  VoiceCommandResponse,
  VoiceExecutionResult
} from '../models';
import { VoiceContextService } from './voice-context.service';
import { VoiceSessionService } from './voice-session.service';
import { IntentValidatorService } from './intent-validator.service';
import { ServiceRouterService } from './service-router.service';
import { VoiceClarificationService } from './voice-clarification.service';
import { VoiceTelemetryService } from './voice-telemetry.service';
import { VoiceIntent, VoiceMessageLink, SuggestedAction, VoiceConfirmationCard } from '../models';
import { ALLOWED_ENDPOINTS, QUICK_ACTIONS, CONTEXT_DEFAULT_PARAMS_BY_ENDPOINT, BATCHABLE_ENDPOINTS, MAX_BATCH_EXTRA } from '../constants';
import { batchSummaryHeader, droppedBatchNotice } from './batch-summary';

const CONFIRM_PREFIX = 'voice-confirm:';
const CANCEL_PREFIX = 'voice-cancel:';

/** Date ISO (YYYY-MM-DD) lisible : « jeudi 15 octobre ». */
function formatIsoDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

const PORTION_LABELS: Record<string, string> = {
  FullDay: 'journée entière',
  Morning: 'matin',
  Afternoon: 'après-midi',
  Evening: 'soir'
};

/**
 * Ce qui va être fait, avec les valeurs de l'intent (jamais le texte libre du
 * modèle, qui pourrait diverger des paramètres réellement exécutés).
 */
export function describeIntentForConfirmation(intent: VoiceIntent): { title: string; details: string[] } {
  const p = intent.params;
  const details: string[] = [];

  switch (intent.endpoint) {
    case 'project.deleteTask': {
      const taskTitle = typeof p['taskTitle'] === 'string' ? p['taskTitle'].trim() : '';
      if (typeof p['projectName'] === 'string' && p['projectName'].trim()) {
        details.push(`Projet : ${p['projectName'].trim()}`);
      }
      details.push('Cette suppression est définitive.');
      return { title: `Supprimer la tâche « ${taskTitle} » ?`, details };
    }
    case 'calendar.removePresences': {
      const dates = Array.isArray(p['dates']) ? (p['dates'] as string[]).map(formatIsoDate) : [];
      const portion = typeof p['portion'] === 'string' ? PORTION_LABELS[p['portion']] : undefined;
      if (portion) details.push(`Période : ${portion}`);
      const list = dates.length > 0 ? dates.join(', ') : 'les dates indiquées';
      return {
        title: dates.length > 1 ? `Supprimer vos présences du ${list} ?` : `Supprimer votre présence du ${list} ?`,
        details
      };
    }
    default:
      return { title: `${ALLOWED_ENDPOINTS[intent.endpoint]?.description ?? 'Exécuter cette action'} ?`, details };
  }
}

/**
 * Service principal d'orchestration des commandes vocales
 * Coordonne la capture audio, l'API, la session conversationnelle et l'exécution des intents
 */
@Injectable({ providedIn: 'root' })
export class VoiceService {
  private audioCapture = inject(AudioCaptureService);
  private voiceApi = inject(VoiceApiService);
  private voiceContext = inject(VoiceContextService);
  private voiceSession = inject(VoiceSessionService);
  private validator = inject(IntentValidatorService);
  private router = inject(ServiceRouterService);
  /**
   * Injecté ici (et NON `VoiceGuidedFlowService`, qui injecte `VoiceService` —
   * voir la JSDoc de `VoiceClarificationService` pour le raisonnement complet
   * anti-cycle DI) : ce service reste à sens unique, `VoiceService` ne dépend
   * jamais de `VoiceGuidedFlowService`.
   */
  private clarification = inject(VoiceClarificationService);
  private telemetry = inject(VoiceTelemetryService);

  // État interne
  private _state = signal<VoiceState>(INITIAL_VOICE_STATE);

  // Selectors publics
  state = computed(() => this._state());
  status = computed(() => this._state().status);
  transcript = computed(() => this._state().transcript);
  response = computed(() => this._state().response);
  error = computed(() => this._state().error);
  audioLevel = computed(() => this.audioCapture.audioLevel());
  duration = computed(() => this.audioCapture.duration());

  // États dérivés
  isIdle = computed(() => this.status() === 'idle');
  isRecording = computed(() => this.status() === 'recording');
  isProcessing = computed(() => this.status() === 'processing');
  isSpeaking = computed(() => this.status() === 'speaking');
  isActive = computed(() => !this.isIdle());
  hasError = computed(() => this.status() === 'error');

  /**
   * MOKA-208 P5 : `true` quand la dernière commande a échoué en mode dégradé
   * générique (edge function injoignable même après le retry déjà tenté par
   * `VoiceApiService`) — PAS pour un `errorCode: 'quota_exceeded'`, qui a son
   * propre traitement (cf. `executeAndRespond`), ni pour une simple
   * incompréhension GPT (`response.success === true`, `intent: null`).
   * Remis à `false` à deux points, choisis pour qu'aucune trace du mode
   * dégradé ne survive à une action qui a réellement fonctionné : (1) au
   * début de `startCommand`/`sendTextCommand`, comme `error`/`transcript`
   * plus bas, pour qu'une nouvelle tentative reparte sans bandeau résiduel
   * pendant qu'elle est en cours ; (2) dès qu'une réponse arrive avec
   * `success: true` dans `executeAndRespond`, confirmation définitive que
   * l'edge function répond de nouveau.
   */
  #assistantUnavailable = signal(false);
  assistantUnavailable = computed(() => this.#assistantUnavailable());

  /**
   * Dernière demande écrite qui n'a pas abouti (MOKA-363) : « Réessayer » la renvoie sans
   * la réécrire ni doubler sa bulle. Effacée dès qu'une demande aboutit.
   */
  #lastFailedText = signal<string | null>(null);
  lastFailedText = computed(() => this.#lastFailedText());

  /**
   * Vérifie si le navigateur supporte les commandes vocales
   */
  get isSupported(): boolean {
    return this.audioCapture.isSupported;
  }

  /**
   * Démarre une nouvelle commande vocale
   * Enregistre l'audio, l'envoie à Whisper, parse l'intention
   */
  async startCommand(): Promise<VoiceCommandResponse | null> {
    if (!this.isIdle()) {
      return null;
    }

    this.#dropPendingConfirmation();
    this.updateState({ status: 'recording', error: null, transcript: '', response: '' });
    this.#assistantUnavailable.set(false);

    try {

      // Appliquer la config audio du module courant
      const moduleConfig = this.voiceContext.moduleVoiceConfig();
      if (Object.keys(moduleConfig).length > 0) {
        this.audioCapture.configure(moduleConfig);
      }

      // Enregistrer l'audio
      const audioBlob = await this.audioCapture.startRecording();

      // Passer en mode traitement
      this.updateState({ status: 'processing' });

      const context = this.buildContext();
      const session = this.voiceSession.buildSessionPayload();

      // Envoyer à l'Edge Function (Whisper + GPT)
      const result = await this.voiceApi.transcribe(audioBlob, context, session);

      if (!result.success) {
        this.updateState({
          status: 'error',
          error: result.error || 'Erreur de transcription'
        });
        return result;
      }

      this.updateState({
        status: 'idle',
        transcript: result.transcript
      });

      if (result.transcript) {
        this.voiceSession.addMessage('user', result.transcript, 'voice');
      }

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      this.updateState({ status: 'error', error: message });
      return null;
    }
  }

  /**
   * Envoie une commande texte (saisie écrite dans le fil de conversation), sans
   * capture audio ni Whisper — le transcript retourné est le texte saisi
   */
  async sendTextCommand(text: string, options: { replay?: boolean } = {}): Promise<VoiceCommandResponse | null> {
    this.#dropPendingConfirmation();
    // Un renvoi (« Réessayer ») garde la bulle déjà affichée.
    if (!options.replay) this.voiceSession.addMessage('user', text, 'text');
    this.updateState({ status: 'processing', error: null, transcript: text, response: '' });
    this.#assistantUnavailable.set(false);

    try {
      const context = this.buildContext();
      const session = this.voiceSession.buildSessionPayload();

      const result = await this.voiceApi.sendText(text, context, session);

      if (!result.success) {
        // Le quota ne se résout pas en réessayant tout de suite : pas de « Réessayer ».
        this.#lastFailedText.set(result.errorCode === 'quota_exceeded' ? null : text);
        this.updateState({
          status: 'error',
          error: result.error || 'Votre demande n\'a pas pu être traitée.'
        });
        return result;
      }

      this.#lastFailedText.set(null);
      this.updateState({
        status: 'idle',
        transcript: text
      });

      return result;
    } catch {
      this.#lastFailedText.set(text);
      this.updateState({ status: 'error', error: 'Votre demande n\'a pas pu être envoyée. Vérifiez votre connexion internet.' });
      return null;
    }
  }

  /**
   * Centralise ce qui suit un `startCommand`/`sendTextCommand` réussi : alimente le
   * fil de conversation de la session, valide puis route l'intent, et restitue la
   * réponse (TTS en mode vocal, mise à jour d'état seule en mode texte).
   */
  async executeAndRespond(
    response: VoiceCommandResponse,
    inputMode: 'voice' | 'text'
  ): Promise<VoiceExecutionResult | null> {
    if (!response.success) {
      // MOKA-208 P5 : deux motifs d'échec distincts, deux traitements.
      if (response.errorCode === 'quota_exceeded') {
        // Quota de l'assistant atteint : message dédié basé sur l'erreur
        // renvoyée par l'edge function (déjà retenue SANS retry côté
        // VoiceApiService — un quota ne se résout pas en réessayant tout de
        // suite). Ce n'est PAS le mode dégradé générique : on ne pose pas
        // `assistantUnavailable`, seulement les mêmes chips d'actions
        // rapides que `#respondUnsupported` pour garder les flows guidés
        // utilisables (ils ne passent pas par GPT).
        const message = response.error || 'Quota de requêtes de l\'assistant atteint.';
        const options = QUICK_ACTIONS.filter(a => a.primary).map(a => ({ id: a.id, label: a.label }));
        this.telemetry.track({
          eventType: 'failed',
          inputMode,
          failureReason: 'quota_exceeded'
        });
        this.voiceSession.addMessage('assistant', message, inputMode, options);
        await this.respond(message, inputMode);
        return null;
      }

      // Mode dégradé générique : edge function injoignable même après le
      // retry déjà tenté par `VoiceApiService`. Mêmes chips que ci-dessus
      // (et que `#respondUnsupported`) pour que l'utilisateur continue par
      // boutons ; `assistantUnavailable` passe à `true` pour que l'UI puisse
      // s'adapter (cf. sa JSDoc pour les points de remise à `false`).
      const message = 'L\'assistant est momentanément indisponible.';
      const options = QUICK_ACTIONS.filter(a => a.primary).map(a => ({ id: a.id, label: a.label }));
      this.#assistantUnavailable.set(true);
      this.telemetry.track({
        eventType: 'failed',
        inputMode,
        failureReason: 'assistant_indisponible'
      });
      this.voiceSession.addMessage('assistant', message, inputMode, options);
      await this.respond(message, inputMode);
      return null;
    }

    this.#assistantUnavailable.set(false);

    if (!response.intent) {
      const message = 'Je n\'ai pas compris votre demande.';
      this.telemetry.track({
        eventType: 'unsupported',
        transcript: response.transcript,
        inputMode
      });
      this.voiceSession.addMessage('assistant', message, inputMode);
      await this.respond(message, inputMode);
      return null;
    }

    const intent = response.intent;

    // session.dismiss : refus poli d'un follow-up, traité en no-op ici (pas de handler module)
    if (intent.endpoint === 'session.dismiss') {
      const message = 'Très bien.';
      this.voiceSession.clearPendingFollowUp();
      this.voiceSession.addMessage('assistant', message, inputMode);
      await this.respond(message, inputMode);
      return { success: true, message };
    }

    // MOKA-208 P4 : une phrase peut porter plusieurs créations. Le garde-fou
    // est ici, pas seulement dans le prompt : on refuse d'enchaîner si une
    // action demande confirmation ou n'est pas une création — un modèle qui
    // se tromperait ne doit pas pouvoir déclencher une suppression en série.
    const extra = (intent.additionalIntents ?? []).filter(i => this.#isBatchable(i));
    if (extra.length > 0 && this.#isBatchable(intent)) {
      return this.#executeBatch(
        [intent, ...extra.slice(0, MAX_BATCH_EXTRA)],
        inputMode,
        Math.max(0, extra.length - MAX_BATCH_EXTRA)
      );
    }

    // MOKA-299 : une action marquée `requiresConfirmation` (ou jugée sensible
    // par le modèle) ne part jamais d'une phrase seule : carte « Confirmer /
    // Annuler » dans le fil, exécution à la confirmation. La garde est ICI —
    // chemin libre (voix/texte) — et non dans `executeIntent`, que les flux
    // guidés, la clarification et les alternatives rejouent : le flux guidé de
    // suppression a déjà sa propre confirmation, pas de seconde demande.
    if (this.validator.isEndpointAllowed(intent.endpoint)
      && this.validator.validate(intent).valid
      && this.validator.requiresConfirmation(intent)) {
      return this.#askConfirmation(intent, inputMode);
    }

    return this.executeIntent(intent, inputMode);
  }

  /** Confirmation en attente (une seule à la fois : une nouvelle demande remplace l'ancienne). */
  #pendingConfirmation: { id: string; intent: VoiceIntent; inputMode: 'voice' | 'text' } | null = null;
  readonly #pendingConfirmationId = signal<string | null>(null);
  /** Identifiant de la carte de confirmation encore active (les boutons des autres sont masqués). */
  readonly pendingConfirmationId = this.#pendingConfirmationId.asReadonly();

  /** `true` si cet identifiant de bouton appartient à une carte de confirmation (active ou périmée). */
  isConfirmationOption(id: string): boolean {
    return id.startsWith(CONFIRM_PREFIX) || id.startsWith(CANCEL_PREFIX);
  }

  async #askConfirmation(intent: VoiceIntent, inputMode: 'voice' | 'text'): Promise<VoiceExecutionResult | null> {
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    this.#pendingConfirmation = { id, intent, inputMode };
    this.#pendingConfirmationId.set(id);

    const { title, details } = describeIntentForConfirmation(intent);
    const card: VoiceConfirmationCard = {
      id,
      title,
      details,
      confirmOption: { id: `${CONFIRM_PREFIX}${id}`, label: 'Confirmer' },
      cancelOption: { id: `${CANCEL_PREFIX}${id}`, label: 'Annuler' }
    };

    this.voiceSession.clearPendingFollowUp();
    this.voiceSession.addMessage('assistant', title, inputMode, undefined, true, undefined, card);
    await this.respond(title, inputMode);
    return null;
  }

  /**
   * Réponse à la carte de confirmation. « Confirmer » exécute l'intent UNE fois
   * (la confirmation est consommée avant l'exécution) ; « Annuler » ne l'exécute
   * jamais. Un clic sur une carte périmée est ignoré.
   */
  async resolveConfirmation(optionId: string): Promise<VoiceExecutionResult | null> {
    const confirmed = optionId.startsWith(CONFIRM_PREFIX);
    const id = optionId.slice(confirmed ? CONFIRM_PREFIX.length : CANCEL_PREFIX.length);
    const pending = this.#pendingConfirmation;
    if (!pending || pending.id !== id) return null;

    this.#pendingConfirmation = null;
    this.#pendingConfirmationId.set(null);

    this.voiceSession.addMessage('user', confirmed ? 'Confirmer' : 'Annuler', 'text', undefined, false);

    if (!confirmed) {
      const message = 'D\'accord, j\'ai annulé : rien n\'a été modifié.';
      this.voiceSession.addMessage('assistant', message, 'text', undefined, false);
      this.updateState({ status: 'idle', response: message });
      return { success: true, message };
    }

    return this.executeIntent(pending.intent, pending.inputMode);
  }

  #dropPendingConfirmation(): void {
    this.#pendingConfirmation = null;
    this.#pendingConfirmationId.set(null);
  }

  /**
   * Seules les créations s'enchaînent sans confirmation : elles sont additives
   * et réparables. Tout le reste (suppression, changement de statut,
   * assignation) reste une action isolée.
   */
  #isBatchable(intent: VoiceIntent): boolean {
    return BATCHABLE_ENDPOINTS.includes(intent.endpoint) && !intent.confirmationRequired;
  }

  /**
   * Exécute une suite d'actions en séquence (jamais en parallèle : les handlers
   * partagent le contexte de session et les numéros de tâche doivent rester
   * ordonnés). Une action qui échoue n'interrompt pas les suivantes — mieux
   * vaut créer deux tâches sur trois et le dire que tout abandonner.
   */
  async #executeBatch(
    intents: VoiceIntent[],
    inputMode: 'voice' | 'text',
    dropped = 0
  ): Promise<VoiceExecutionResult | null> {
    const results: VoiceExecutionResult[] = [];

    for (const intent of intents) {
      this.#applySessionContextDefaults(intent);
      const validation = this.validator.validate(intent);
      if (!validation.valid) {
        results.push({ success: false, message: `Action ignorée : ${validation.errors.join('. ')}` });
        continue;
      }

      const result = await this.router.execute(intent);
      this.telemetry.track({
        eventType: result.success ? 'executed' : 'failed',
        endpoint: intent.endpoint,
        inputMode,
        confidence: intent.confidence,
        failureReason: result.success ? null : result.message
      });
      results.push(result);

      if (result.success && result.data) {
        this.voiceSession.setEntitiesFromResult(result.data);
      }
    }

    const succeeded = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);

    // Un lot partiellement exécuté est le cas le plus traître : l'utilisateur
    // voit bien le détail dans la réponse, mais sans marqueur dédié ces
    // exécutions partielles seraient indistinguables d'échecs isolés dans la
    // télémétrie. C'est LE signal à surveiller sur les intents multiples.
    if (succeeded.length > 0 && failed.length > 0) {
      this.telemetry.track({
        eventType: 'failed',
        endpoint: 'batch.partial',
        inputMode,
        failureReason: `partiel_${succeeded.length}_sur_${results.length}`
      });
    }

    const lines = succeeded.map(r => `• ${r.message}`);
    if (failed.length > 0) {
      lines.push(...failed.map(r => `• (échec) ${r.message}`));
    }

    // MOKA-266 : en-tête qui dit ce qui a été fait (« 2 réservations faites »,
    // « 1 réservation sur 2 »), et jamais de demande écartée en silence.
    const header = batchSummaryHeader(intents.map(i => i.endpoint), succeeded.length, results.length);
    const notice = droppedBatchNotice(dropped, MAX_BATCH_EXTRA + 1);
    const message = `${header}\n${lines.join('\n')}${notice ? `\n${notice}` : ''}`;

    // Les alternatives d'un échec (autre salle, créneau voisin) restent
    // cliquables dans un lot, comme pour une action isolée (MOKA-208 P2 #2).
    const suggestions = failed.flatMap(r => r.suggestedActions ?? []).slice(0, 4);
    this.#pendingSuggestions.clear();
    for (const action of suggestions) {
      this.#pendingSuggestions.set(action.id, action);
    }
    const options = suggestions.length > 0 ? suggestions.map(a => ({ id: a.id, label: a.label })) : undefined;

    // Pas de follow-up après un lot : enchaîner « voulez-vous l'assigner ? »
    // sur trois créations d'un coup serait ambigu (laquelle ?).
    this.voiceSession.clearPendingFollowUp();
    this.voiceSession.addMessage('assistant', message, inputMode, options);
    await this.respond(message, inputMode);

    return { success: failed.length === 0, message };
  }

  /**
   * Point d'entrée unique d'exécution d'un intent déjà construit (validation,
   * routage, gestion du follow-up/des entités, restitution de la réponse).
   * Extrait de `executeAndRespond` (MOKA-206 Phase 3) pour que
   * `VoiceGuidedFlowService` — les flows guidés par boutons (Lot F) et la
   * résolution de la clarification "projet manquant" (Lot H) — puisse exécuter
   * un intent SANS repasser par GPT, avec exactement le même traitement que le
   * chemin vocal/texte classique (aucun nouveau chemin d'exécution, aucune
   * régression sur `executeAndRespond`).
   */
  async executeIntent(intent: VoiceIntent, inputMode: 'voice' | 'text'): Promise<VoiceExecutionResult | null> {
    // Lot E (retour terrain) : `session.unsupported` — envoyé par GPT quand la
    // demande ne correspond à aucun endpoint connu — est traité en no-op AVANT
    // la validation générique, comme `session.dismiss` l'est dans
    // `executeAndRespond`. Jamais de `router.execute` pour cet endpoint.
    if (intent.endpoint === 'session.unsupported') {
      return this.#respondUnsupported(inputMode);
    }

    const validation = this.validator.validate(intent);

    if (!validation.valid) {
      // Filet de sécurité (Lot E) : un endpoint réellement inconnu (pas
      // whitelisté du tout — cas différent de `session.unsupported`, prévu
      // lui) ne doit jamais afficher l'erreur technique brute à l'utilisateur.
      // Seul CE cas précis (endpoint non autorisé) est remplacé ; les autres
      // échecs de validation (paramètres manquants, clarification requise)
      // gardent leur traitement inchangé ci-dessous.
      if (!this.validator.isEndpointAllowed(intent.endpoint)) {
        console.warn('[VoiceService] Endpoint non autorisé:', intent.endpoint, validation.errors);
        return this.#respondUnsupported(inputMode, intent.endpoint);
      }

      const message = intent.needsClarification
        ? intent.clarificationQuestion || 'Je n\'ai pas compris votre demande.'
        : `Erreur: ${validation.errors.join('. ')}`;
      this.telemetry.track({
        eventType: 'clarification',
        endpoint: intent.endpoint,
        transcript: this.#lastUserUtterance(),
        inputMode,
        confidence: intent.confidence,
        failureReason: intent.needsClarification ? 'clarification_demandee' : 'parametres_invalides'
      });
      this.voiceSession.addMessage('assistant', message, inputMode);
      await this.respond(message, inputMode);
      return null;
    }

    // MOKA-208 P2 #3 : compléter l'intent avec les contextes posés (sprint, lot)
    // AVANT exécution. GPT reçoit bien ces entités mais ne les reporte pas de
    // façon fiable dans les params — même constat que pour `taskId` en Phase 2 :
    // quand le front connaît la valeur, il la pose lui-même plutôt que de parier
    // sur le modèle. Les flows guidés font déjà cela de leur côté (avec récap).
    this.#applySessionContextDefaults(intent);

    const execResult = await this.router.execute(intent);

    // Lot C (MOKA-206 Phase 3) : échec faute de projet précisé → clarification
    // par boutons plutôt qu'un message d'échec sec. Court-circuite le
    // traitement normal du followUp/des entités (rejoué une fois la
    // clarification résolue, via un nouvel appel à `executeIntent`).
    if (!execResult.success && execResult.needsProjectClarification) {
      const options = await this.clarification.buildProjectOptions();
      this.clarification.setPendingClarification(intent);
      this.voiceSession.addMessage('assistant', execResult.message, inputMode, options);
      await this.respond(execResult.message, inputMode);
      return execResult;
    }

    // MOKA-208 P2 #2 : alternatives cliquables après un échec récupérable.
    // Elles sont affichées comme des boutons et rejouées telles quelles, sans
    // repasser par GPT ni redemander ce qui est déjà connu.
    if (!execResult.success && execResult.suggestedActions?.length) {
      this.#pendingSuggestions.clear();
      for (const action of execResult.suggestedActions) {
        this.#pendingSuggestions.set(action.id, action);
      }
      const options = execResult.suggestedActions.map(a => ({
        id: a.id,
        label: a.label,
        emphasis: a.emphasis,
        hint: a.hint
      }));
      // Une question de choix (quel calendrier ?) n'est pas un échec : elle ne
      // doit pas fausser la mesure des demandes qui n'aboutissent pas (MOKA-208).
      this.telemetry.track(execResult.awaitingChoice
        ? {
          eventType: 'clarification',
          endpoint: intent.endpoint,
          transcript: this.#lastUserUtterance(),
          inputMode,
          confidence: intent.confidence,
          failureReason: 'choix_demande'
        }
        : {
          eventType: 'failed',
          endpoint: intent.endpoint,
          inputMode,
          failureReason: execResult.message
        });
      this.voiceSession.addMessage('assistant', execResult.message, inputMode, options);
      await this.respond(execResult.message, inputMode);
      return execResult;
    }

    this.telemetry.track({
      eventType: execResult.success ? 'executed' : 'failed',
      endpoint: intent.endpoint,
      inputMode,
      confidence: intent.confidence,
      failureReason: execResult.success ? null : execResult.message
    });

    let finalMessage = execResult.message;
    if (execResult.followUp) {
      this.voiceSession.setPendingFollowUp(execResult.followUp);
      finalMessage = `${execResult.message} ${execResult.followUp.question}`;
    } else {
      this.voiceSession.clearPendingFollowUp();
    }

    // MOKA-208 P2 #1 : les handlers exposent déjà les entités citées en données
    // structurées (`data.tasks`) ; on les rend cliquables au lieu de laisser
    // l'utilisateur recopier un numéro de tâche à la main.
    this.voiceSession.addMessage(
      'assistant',
      finalMessage,
      inputMode,
      undefined,
      true,
      this.#linksFromResult(execResult)
    );

    if (execResult.data) {
      this.voiceSession.setEntitiesFromResult(execResult.data);
    }

    await this.respond(finalMessage, inputMode);

    return execResult;
  }

  /**
   * Réponse polie "je ne sais pas faire ça" (Lot E), avec des suggestions
   * cliquables construites DYNAMIQUEMENT à partir de `QUICK_ACTIONS` (labels
   * `primary`) plutôt que hardcodées — si le catalogue change, ce message suit
   * automatiquement. Les chips réutilisent le mécanisme `VoiceChatMessage.options`
   * déjà en place (Lot E du lot initial) ; c'est `VoiceAssistantOverlayComponent`
   * qui les route vers `VoiceGuidedFlowService.start()` au clic (elles ne
   * répondent pas à une étape de flow, contrairement aux autres boutons de
   * réponse rapide — cf. sa JSDoc `onOptionSelected`).
   */
  async #respondUnsupported(
    inputMode: 'voice' | 'text',
    attemptedEndpoint?: string
  ): Promise<VoiceExecutionResult> {
    // MOKA-208 : c'est LA mesure qui compte — savoir ce que l'utilisateur a
    // voulu dire quand l'assistant n'a rien su faire.
    this.telemetry.track({
      eventType: 'unsupported',
      endpoint: attemptedEndpoint ?? null,
      transcript: this.#lastUserUtterance(),
      inputMode
    });

    const options = QUICK_ACTIONS.filter(a => a.primary).map(a => ({ id: a.id, label: a.label }));
    const suggestions = options.map(o => o.label.toLowerCase()).join(', ');
    const message = `Je ne sais pas encore faire ça. Essayez par exemple : ${suggestions}.`;

    this.voiceSession.addMessage('assistant', message, inputMode, options);
    await this.respond(message, inputMode);

    return { success: true, message };
  }

  /**
   * Complète les paramètres absents d'un intent à partir des contextes posés en
   * conversation. N'écrase JAMAIS une valeur explicite : ce qui est dit dans la
   * phrase courante prime toujours sur un contexte posé plus tôt.
   *
   * Le rattachement effectif est annoncé par le handler dans son message de
   * succès — un contexte appliqué en silence serait pire que pas de contexte.
   */
  #applySessionContextDefaults(intent: VoiceIntent): void {
    const supported = CONTEXT_DEFAULT_PARAMS_BY_ENDPOINT[intent.endpoint];
    if (!supported) return;

    const entities = this.voiceSession.entities();
    const available: Record<string, string | undefined> = {
      assigneeName: entities.person?.name,
      dueDate: entities.dueDate,
      groupName: entities.group?.name,
      releaseName: entities.release?.name
    };

    for (const param of supported) {
      if (intent.params[param] === undefined && available[param]) {
        intent.params[param] = available[param];
      }
    }
  }

  /**
   * Alternatives proposées en attente de clic (cf. `suggestedActions`).
   * Volatiles par nature : une alternative de créneau n'a de sens que dans la
   * seconde qui suit l'échec, pas après un rechargement de page.
   */
  #pendingSuggestions = new Map<string, SuggestedAction>();

  /** `true` si cet identifiant de bouton correspond à une alternative en attente. */
  hasSuggestedAction(id: string): boolean {
    return this.#pendingSuggestions.has(id);
  }

  /**
   * Rejoue l'alternative choisie. L'intent est reconstruit à partir de l'action
   * (endpoint + params déjà complets), donc aucun appel à GPT.
   */
  async runSuggestedAction(id: string): Promise<VoiceExecutionResult | null> {
    const action = this.#pendingSuggestions.get(id);
    if (!action) return null;

    this.#pendingSuggestions.clear();
    this.voiceSession.addMessage('user', action.label, 'text', undefined, false);

    return this.executeIntent(
      {
        endpoint: action.endpoint,
        method: 'POST',
        params: action.params,
        confirmationRequired: false,
        responseMessage: action.label,
        confidence: 1,
        needsClarification: false,
        clarificationQuestion: null
      },
      'text'
    );
  }

  /**
   * Extrait les liens de navigation d'un résultat d'exécution. Seule la forme
   * `data.tasks` (listMyTasks) est reconnue aujourd'hui ; d'autres handlers
   * pourront alimenter le même champ sans toucher au rendu.
   */
  #linksFromResult(result: VoiceExecutionResult): VoiceMessageLink[] | undefined {
    const data = result.data as { tasks?: { label?: string; route?: string }[] } | undefined;
    const tasks = data?.tasks;
    if (!Array.isArray(tasks) || tasks.length === 0) return undefined;

    const links = tasks
      .filter(t => typeof t?.label === 'string' && typeof t?.route === 'string')
      .map(t => ({ label: t.label as string, route: t.route as string }));

    return links.length > 0 ? links : undefined;
  }

  /**
   * Dernière phrase prononcée/saisie par l'utilisateur, lue dans le fil de la
   * session plutôt que propagée en paramètre : `executeIntent` est aussi appelé
   * par les flows guidés et la clarification, qui n'ont pas de transcript.
   */
  #lastUserUtterance(): string | null {
    const messages = this.voiceSession.messages();
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].content;
    }
    return null;
  }

  /**
   * Arrête l'enregistrement en cours
   */
  stopRecording(): void {
    if (this.isRecording()) {
      this.audioCapture.stopRecording();
    }
  }

  /**
   * Annule complètement la commande en cours
   */
  cancel(): void {
    this.audioCapture.cancelRecording();
    this.updateState(INITIAL_VOICE_STATE);
  }

  /**
   * Réinitialise l'état après une erreur
   */
  reset(): void {
    this.updateState(INITIAL_VOICE_STATE);
  }

  /**
   * Lit un message avec TTS
   */
  async speak(message: string): Promise<void> {
    this.updateState({ status: 'speaking', response: message });

    try {
      const audioBlob = await this.voiceApi.textToSpeech(message);
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      await new Promise<void>((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          reject(new Error('Erreur de lecture audio'));
        };
        audio.play().catch(reject);
      });

      this.updateState({ status: 'idle' });
    } catch (err) {
      // En cas d'erreur TTS, on continue quand même
      console.error('Erreur TTS:', err);
      this.updateState({ status: 'idle' });
    }
  }

  /**
   * Restitue la réponse : lecture TTS en mode vocal, simple mise à jour d'état en mode texte
   * (politique TTS stricte : jamais de lecture audio pour une commande saisie au clavier)
   */
  private async respond(message: string, inputMode: 'voice' | 'text'): Promise<void> {
    if (inputMode === 'voice') {
      await this.speak(message);
    } else {
      this.updateState({ status: 'idle', response: message });
    }
  }

  /**
   * Construit le contexte module/projet courant pour l'Edge Function.
   *
   * MOKA-206 Lot F : un contexte de page écarté par l'utilisateur (croix sur
   * un chip de contexte, `VoiceSessionService.ignoredPageContext`) ne doit
   * plus être envoyé à GPT — sinon l'assistant continuerait de résoudre
   * silencieusement sur ce projet/calendrier malgré la croix. 'project' n'a
   * qu'une IDENTITÉ à retirer (`projectName`), la catégorie de page
   * (`module: 'project'`) reste envoyée telle quelle. 'calendar' n'a pas
   * d'identité fine en dehors du module lui-même : le contexte entier est
   * omis (`undefined`), exactement comme si `activeModule` était `null`.
   */
  private buildContext(): { module: string; projectName?: string } | undefined {
    const activeModule = this.voiceContext.activeModule();
    if (!activeModule) return undefined;

    const ignored = this.voiceSession.ignoredPageContext();

    if (activeModule === 'calendar' && ignored.has('calendar')) {
      return undefined;
    }

    const projectName = activeModule === 'project' && !ignored.has('project')
      ? this.voiceContext.currentProjectName() ?? undefined
      : undefined;

    return { module: activeModule, projectName };
  }

  /**
   * Met à jour l'état partiellement
   */
  private updateState(partial: Partial<VoiceState>): void {
    this._state.update(current => ({ ...current, ...partial }));
  }
}
